import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CommandPalette from './CommandPalette';

interface GlobalSearchContextValue {
    /** Open the palette (the top-bar trigger). */
    open: () => void;
}

const GlobalSearchContext = createContext<GlobalSearchContextValue>({ open: () => undefined });

export const useGlobalSearch = () => useContext(GlobalSearchContext);

/** True for ⌘K on macOS and Ctrl+K elsewhere. */
export const isSearchShortcut = (event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'>) =>
    event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey;

/**
 * The global search (#3016): ⌘K / Ctrl+K anywhere in the layout, or the
 * top-bar trigger, opens one search across bookings, customers, objects and
 * products. Navigating closes it — the result belongs to the page it opened.
 */
export const GlobalSearchProvider = ({ children }: { children: React.ReactNode }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { pathname } = useLocation();

    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (!isSearchShortcut(event)) return;
            // The browser's own ⌘K / Ctrl+K (search bar focus) gives way.
            event.preventDefault();
            setIsOpen((current) => !current);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        setIsOpen(false);
    }, [pathname]);

    const value = useMemo(() => ({ open }), [open]);

    return (
        <GlobalSearchContext.Provider value={value}>
            {children}
            {isOpen && <CommandPalette onClose={close} />}
        </GlobalSearchContext.Provider>
    );
};
